import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, FilePdf, ArrowUpRight, PaperPlaneTilt } from "@phosphor-icons/react";
import { api, POUND, formatApiError, TOKEN_KEY } from "../lib/api";
import { toast } from "sonner";
import EmailQuoteDialog from "../components/EmailQuoteDialog";

const STATUS_OPTIONS = ["Draft", "Sent", "Accepted", "In Progress", "Completed", "Rejected"];
const STATUS_CLASS = {
  Draft: "badge-draft",
  Sent: "badge-sent",
  Accepted: "badge-accepted",
  "In Progress": "badge-progress",
  Completed: "badge-completed",
  Rejected: "badge-rejected",
};

export default function Quotes() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("All");
  const [emailing, setEmailing] = useState(null); // { quote, customer }
  const [customers, setCustomers] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [q, c] = await Promise.all([api.get("/quotes"), api.get("/customers")]);
        setItems(q.data);
        setCustomers(c.data);
      }
      finally { setLoading(false); }
    })();
  }, []);

  const openEmail = (q) => {
    const cust = customers.find((c) => c.id === q.customer_id) || { name: q.customer_name, email: "" };
    setEmailing({ quote: q, customer: cust });
  };

  const updateStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/quotes/${id}/status`, { status });
      setItems((x) => x.map((q) => (q.id === id ? data : q)));
      toast.success(`Status → ${status}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const downloadPdf = async (q) => {
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/quotes/${q.id}/pdf`;
      const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {}, credentials: "include" });
      if (!res.ok) throw new Error("Failed to generate PDF");
      const blob = await res.blob();
      const u = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = u;
      a.download = `quote-${q.quote_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(u);
    } catch (e) { toast.error("Failed to download PDF"); }
  };

  const filtered = filter === "All" ? items : items.filter((q) => q.status === filter);

  return (
    <div className="p-6 lg:p-10 max-w-[1400px]">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-8">
        <div>
          <span className="hero-rule" />
          <div className="overline">Estimating</div>
          <h1 className="font-display text-4xl lg:text-5xl font-bold tracking-tight mt-2">Quotes</h1>
        </div>
        <Link to="/quotes/new" className="btn-primary flex items-center gap-2" data-testid="quotes-new-button">
          <Plus size={16} weight="bold" /> New Quote
        </Link>
      </div>

      <div className="flex gap-1.5 mb-4 flex-wrap">
        {["All", ...STATUS_OPTIONS].map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wider border rounded-sm transition-colors ${
              filter === s ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:border-slate-500"
            }`}
            data-testid={`quote-filter-${s}`}
          >
            {s}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-sm text-slate-500" data-testid="quotes-loading">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="card-elev p-12 text-center">
          <div className="font-display text-xl font-semibold">No quotes yet</div>
          <p className="text-sm text-slate-500 mt-1">Create a quote to get started.</p>
          <Link to="/quotes/new" className="btn-primary inline-block mt-4" data-testid="quotes-empty-cta">+ New Quote</Link>
        </div>
      ) : (
        <div className="card-elev overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="overline text-left p-3">Number</th>
                <th className="overline text-left p-3">Customer</th>
                <th className="overline text-left p-3">Roof Type</th>
                <th className="overline text-left p-3">Status</th>
                <th className="overline text-right p-3">Total</th>
                <th className="overline text-right p-3">Updated</th>
                <th className="overline text-right p-3"></th>
              </tr>
            </thead>
            <tbody className="font-mono-data">
              {filtered.map((q) => (
                <tr key={q.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`quote-row-${q.id}`}>
                  <td className="p-3 font-semibold">{q.quote_number}</td>
                  <td className="p-3 font-sans">{q.customer_name}</td>
                  <td className="p-3 font-sans text-slate-600 text-xs uppercase">{q.roof_type}</td>
                  <td className="p-3">
                    <select
                      value={q.status}
                      onChange={(e) => updateStatus(q.id, e.target.value)}
                      data-testid={`quote-status-select-${q.id}`}
                      className={`${STATUS_CLASS[q.status] || "badge-draft"} text-[10px] font-bold uppercase tracking-wider rounded-sm px-2 py-1 border cursor-pointer`}
                    >
                      {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="p-3 text-right font-semibold">{POUND((q.summary || {}).total || 0)}</td>
                  <td className="p-3 text-right text-xs text-slate-500">{(q.updated_at || "").slice(0, 10)}</td>
                  <td className="p-3">
                    <div className="flex justify-end gap-1">
                      <button className="btn-secondary !px-2 !py-1" onClick={() => openEmail(q)} title="Email PDF" data-testid={`quote-email-${q.id}`}><PaperPlaneTilt size={14} weight="bold" /></button>
                      <button className="btn-secondary !px-2 !py-1" onClick={() => downloadPdf(q)} data-testid={`quote-pdf-${q.id}`}><FilePdf size={14} weight="bold" /></button>
                      <Link className="btn-secondary !px-2 !py-1" to={`/quotes/${q.id}`} data-testid={`quote-open-${q.id}`}><ArrowUpRight size={14} weight="bold" /></Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <EmailQuoteDialog
        open={!!emailing}
        onClose={() => setEmailing(null)}
        quote={emailing?.quote}
        customer={emailing?.customer}
        onSent={(res) => {
          // Promote Draft -> Sent locally
          if (emailing?.quote?.status === "Draft") {
            setItems((x) => x.map((q) => q.id === emailing.quote.id ? { ...q, status: "Sent" } : q));
          }
        }}
      />
    </div>
  );
}
